import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ClipboardList, Calendar, FileText, RefreshCw, Clock, MapPin, Bug, Download,
  Loader2, Plus, Leaf, LogOut,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/data/site";

export const STATUS_COLORS = {
  "New Request": "bg-blue-100 text-blue-700",
  "Inspection Scheduled": "bg-amber-100 text-amber-700",
  "Inspection Complete": "bg-teal-100 text-teal-700",
  "Estimate Sent": "bg-purple-100 text-purple-700",
  "Treatment Scheduled": "bg-orange-100 text-orange-700",
  "Treatment Completed": "bg-green-100 text-green-700",
  "Follow-up Required": "bg-red-100 text-red-700",
  "Closed": "bg-gray-200 text-gray-700",
};

export default function Portal() {
  const { user, logout } = useAuth();
  const [items, setItems] = useState(null);
  const [followId, setFollowId] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/portal/inspections").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const requestFollowUp = async () => {
    setBusy(true);
    try {
      await api.post(`/portal/inspections/${followId}/follow-up`, { note });
      toast.success("Follow-up requested. We'll be in touch soon.");
      setFollowId(null); setNote(""); load();
    } catch { toast.error("Could not submit follow-up."); }
    finally { setBusy(false); }
  };

  const stats = items ? {
    total: items.length,
    active: items.filter((i) => !["Closed", "Treatment Completed"].includes(i.status)).length,
    completed: items.filter((i) => i.status === "Treatment Completed" || i.status === "Closed").length,
  } : { total: 0, active: 0, completed: 0 };

  return (
    <div className="pt-28 pb-24 min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <p className="text-xs uppercase tracking-widest text-brand-gold font-semibold">Customer Portal</p>
            <h1 className="font-heading text-3xl font-extrabold text-brand-green mt-1">Hi, {user?.name?.split(" ")[0]} 👋</h1>
            <p className="text-brand-muted mt-1">Track your requests, appointments, and treatment history.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/request-inspection"><Button data-testid="portal-new-request-btn" className="rounded-full bg-brand-green text-white font-semibold hover:bg-brand-greenHover"><Plus className="h-4 w-4 mr-2" /> New Request</Button></Link>
            <Button variant="outline" onClick={logout} className="rounded-full"><LogOut className="h-4 w-4 mr-2" /> Logout</Button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-8">
          {[["Total Requests", stats.total, ClipboardList], ["Active", stats.active, Clock], ["Completed", stats.completed, Leaf]].map(([l, v, Icon]) => (
            <div key={l} className="bg-white rounded-2xl p-5 border border-black/5 shadow-sm">
              <Icon className="h-5 w-5 text-brand-gold" />
              <p className="font-heading text-3xl font-extrabold text-brand-green mt-3">{v}</p>
              <p className="text-brand-muted text-sm">{l}</p>
            </div>
          ))}
        </div>

        {items === null ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-brand-green" /></div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 border border-black/5 shadow-sm text-center">
            <Bug className="h-12 w-12 text-brand-muted/40 mx-auto" />
            <h3 className="font-heading text-xl font-bold text-brand-green mt-4">No requests yet</h3>
            <p className="text-brand-muted mt-2">Requests you submit with this email will appear here.</p>
            <Link to="/request-inspection"><Button className="rounded-full bg-brand-green text-white mt-6 hover:bg-brand-greenHover">Request an Inspection</Button></Link>
          </div>
        ) : (
          <div className="space-y-5" data-testid="portal-requests">
            {items.map((it) => (
              <div key={it.id} className="bg-white rounded-3xl p-6 border border-black/5 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="font-heading font-bold text-brand-green">{it.reference_number}</span>
                      <Badge className={`${STATUS_COLORS[it.status]} border-0 rounded-full`}>{it.status}</Badge>
                    </div>
                    <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-sm text-brand-muted">
                      <span className="flex items-center gap-1.5"><Bug className="h-4 w-4" />{it.pest_type} · {it.severity}</span>
                      <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" />{it.address}</span>
                      {it.scheduled_date && <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" />Scheduled: {it.scheduled_date}</span>}
                      {it.assigned_technician && <span className="flex items-center gap-1.5">Tech: {it.assigned_technician}</span>}
                    </div>
                  </div>
                </div>

                {(it.estimate_amount || it.invoice_amount) && (
                  <div className="grid sm:grid-cols-2 gap-3 mt-4">
                    {it.estimate_amount != null && <div className="bg-brand-light rounded-xl p-4"><p className="text-xs text-brand-muted uppercase tracking-wide">Estimate</p><p className="font-heading font-bold text-brand-green text-lg">${it.estimate_amount}</p>{it.estimate_note && <p className="text-xs text-brand-muted mt-1">{it.estimate_note}</p>}</div>}
                    {it.invoice_amount != null && <div className="bg-brand-light rounded-xl p-4 flex items-center justify-between"><div><p className="text-xs text-brand-muted uppercase tracking-wide">Invoice · {it.invoice_status || "Pending"}</p><p className="font-heading font-bold text-brand-green text-lg">${it.invoice_amount}</p></div><Button size="sm" variant="outline" className="rounded-full" onClick={() => toast.success("Invoice downloaded")}><Download className="h-4 w-4" /></Button></div>}
                  </div>
                )}

                {it.treatment_history?.length > 0 && (
                  <div className="mt-4 border-t border-black/5 pt-4">
                    <p className="text-xs uppercase tracking-wide text-brand-muted font-semibold flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> Treatment Reports</p>
                    <ul className="mt-2 space-y-1.5">
                      {it.treatment_history.map((t, i) => (<li key={`${t.at}-${i}`} className="text-sm text-brand-charcoal flex gap-2"><span className="text-brand-gold">•</span>{t.note} <span className="text-brand-muted text-xs">— {new Date(t.at).toLocaleDateString()}</span></li>))}
                    </ul>
                  </div>
                )}

                <div className="mt-4 flex justify-end">
                  <Button data-testid={`portal-followup-${it.id}`} variant="outline" size="sm" onClick={() => setFollowId(it.id)} className="rounded-full text-brand-green border-brand-green/20 hover:bg-brand-green/5">
                    <RefreshCw className="h-4 w-4 mr-2" /> Request Follow-up
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!followId} onOpenChange={(o) => !o && setFollowId(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading text-brand-green">Request a Follow-up Visit</DialogTitle></DialogHeader>
          <Textarea data-testid="followup-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Describe what you're seeing (e.g. pests returned in the kitchen)..." className="rounded-xl min-h-[110px]" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setFollowId(null)} className="rounded-full">Cancel</Button>
            <Button data-testid="followup-submit-btn" onClick={requestFollowUp} disabled={busy} className="rounded-full bg-brand-green text-white hover:bg-brand-greenHover">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
